import React from "react";
import { Button, Form, Segment, Header } from "semantic-ui-react";
import "./AddContactModal.scss";
import { ContactCustomFieldWithVisibility } from "../types/ContactCustomFieldWithVisibility";
import { FormProvider, useForm } from "react-hook-form";
import { postPermitContact } from "../../../queries/permits";
import { useSelector } from "react-redux";
import network from "../../../selectors/network";
import useApiCallWithToast from "../../../hooks/useApiCallWithToast";
import { PermitsCustomFieldTypesNames } from "../../../constants/PermitsCustomFieldTypesNames";
import Modal from "../../../components/global_components/AccessibleModal";
import { ContactFieldsV2 } from "../../contacts/components/ContactFieldsV2";
import { buildNewPermitContactPayload } from "../utils/buildPermitsContactPayload";

interface IAddContactModalProps {
  open: boolean;
  onClose: () => void;
  contactCustomField: ContactCustomFieldWithVisibility | null;
  customFieldType: number | undefined;
  permitsExtensionDefId: number | undefined;
  permitId: number | string;
  contactType: string | undefined;
}

export const AddContactModal = ({
  open,
  onClose,
  contactCustomField,
  customFieldType,
  permitsExtensionDefId,
  permitId,
  contactType,
}: IAddContactModalProps) => {
  const contactInfoOptions = contactCustomField?.visibilityOptions ?? {
    address: "optional",
    company_name: "optional",
    email_address: "optional",
    license_number: "optional",
    phone_number: "optional",
    first_name: "required",
    last_name: "required",
  };
  const formContext = useForm();
  const networkHeaders = useSelector(network.defaultHeaders);

  // mutation to add contact
  const { run: createContact, loading: isCreateContactLoading } = useApiCallWithToast({
    apiCall: () => postPermitContact(networkHeaders),
    queryKey: ["permits", permitId?.toString()],
    successMessage: "Contact added successfully",
    errorMessage: "Error adding contact",
    onSuccess: () => {
      onClose();
    },
    useResponseErrorMessage: true,
  });

  const isAdditionalContactsField = customFieldType === PermitsCustomFieldTypesNames.ADDITIONAL_CONTACTS;
  const onSubmit = (data: any) => {
    const payload = buildNewPermitContactPayload({
      ...data.contact,
      customFieldType: customFieldType,
      permitsExtensionDefId: permitsExtensionDefId,
      permitId: Number(permitId),
      isApplicant: false,
      ...(!isAdditionalContactsField ? { contactType: contactType } : {}), // Add contact type for single contact info fields
    });
    createContact(payload);
  };

  return (
    <Modal onClose={onClose} isOpen={open} top={52} right={40}>
      <Segment basic padded style={{ minWidth: "40vw" }}>
        <Segment attached='top'>
          <Header>Add {contactCustomField?.name ?? "Contact Information"}</Header>
        </Segment>
        <Segment attached>
          <Form>
            <FormProvider {...formContext}>
              <ContactFieldsV2
                autofillCountry={true}
                physicalAddressPrefix='physical'
                showContactTypeField={isAdditionalContactsField}
                showMailingAddressHeader={false}
                showInviteUser={true}
                namePrefix={"contact"}
                showLicenseAndCompanyFields={true}
                visibilityOptions={contactInfoOptions}
                showDivider={false}
              />
            </FormProvider>
          </Form>
        </Segment>
        <Segment secondary attached='bottom' clearing>
          <Button
            loading={isCreateContactLoading}
            disabled={isCreateContactLoading}
            primary
            type='submit'
            onClick={formContext.handleSubmit(onSubmit)}
            floated='right'
          >
            Add
          </Button>
          <Button basic onClick={onClose} floated='right'>
            Cancel
          </Button>
        </Segment>
      </Segment>
    </Modal>
  );
};
